import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ChevronDown, ArrowRight } from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';
export function Hero() {
  const {
    t
  } = useLanguage();
  const ref = useRef<HTMLDivElement>(null);
  const {
    scrollYProgress
  } = useScroll({
    target: ref,
    offset: ['start start', 'end start']
  });
  const y = useTransform(scrollYProgress, [0, 1], ['0%', '20%']);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);
  return <section ref={ref} className="relative min-h-screen w-full overflow-hidden bg-archival-cream" data-id="element-148">
      {/* Geometric Pattern Background */}
      <div className="absolute inset-0 bg-pattern-kente opacity-40 z-0" data-id="element-149" />

      {/* Decorative Side Borders */}
      <div className="absolute top-0 left-0 h-full w-2 md:w-4 bg-repeat-y z-20" style={{
      backgroundImage: 'linear-gradient(to bottom, #D4AF37 50%, #C1502E 50%)',
      backgroundSize: '100% 40px'
    }} data-id="element-150" />
      <div className="absolute top-0 right-0 h-full w-2 md:w-4 bg-repeat-y z-20" style={{
      backgroundImage: 'linear-gradient(to bottom, #C1502E 50%, #D4AF37 50%)',
      backgroundSize: '100% 40px'
    }} data-id="element-151" />

      <div className="relative z-10 max-w-7xl mx-auto px-8 md:px-16 py-12 md:py-16" data-id="element-152">
        {/* Top Bar */}
        <motion.div initial={{
        opacity: 0,
        y: -20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8
      }} className="flex items-center justify-between mb-8 md:mb-12" data-id="element-153">
          <div className="flex items-center space-x-4" data-id="element-154">
            <div className="h-[2px] w-12 bg-earth-terracotta" data-id="element-155" />
            <span className="text-earth-terracotta font-mono text-xs md:text-sm tracking-[0.2em] uppercase font-bold" data-id="element-156">
              {t('hero.est')}
            </span>
          </div>
        </motion.div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center" data-id="element-157">
          {/* Left: Hero Image */}
          <motion.div initial={{
          opacity: 0,
          x: -50
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 1,
          delay: 0.2
        }} style={{
          y,
          opacity
        }} className="relative order-2 lg:order-1" data-id="element-158">
            {/* Image Frame with African-inspired border */}
            <div className="relative bg-white p-3 md:p-4 shadow-2xl border-8 border-white" data-id="element-159">
              <div className="absolute inset-0 border-4 border-charcoal-900 z-10 pointer-events-none" data-id="element-160" />
              <div className="aspect-[4/3] overflow-hidden relative" data-id="element-161">
                <img src="https://cdn.magicpatterns.com/uploads/rtSU3wpaxzV3LgbZJXG7ZB/ChatGPT_Image_13_janv._2026,_21_36_41.png" alt="African Founders - Archival Portrait" className="w-full h-full object-cover" loading="eager" data-id="element-162" />
              </div>
              {/* Corner Gold Accents */}
              <div className="absolute top-2 left-2 w-8 h-8 border-t-4 border-l-4 border-gold z-20" data-id="element-163" />
              <div className="absolute bottom-2 right-2 w-8 h-8 border-b-4 border-r-4 border-gold z-20" data-id="element-164" />
            </div>

            {/* Decorative Geometric Elements */}
            <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-pattern-kente border-2 border-gold -z-10" data-id="element-165" />
            <div className="absolute -top-6 -left-6 w-full h-full border-2 border-earth-terracotta/30 -z-10" data-id="element-166" />
          </motion.div>

          {/* Right: Text Content */}
          <motion.div initial={{
          opacity: 0,
          x: 50
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 1,
          delay: 0.4
        }} className="order-1 lg:order-2" data-id="element-167">
            <h1 className="text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-sans font-black text-charcoal-900 leading-[0.9] tracking-tight mb-6 md:mb-8" data-id="element-168">
              {t('hero.title1')} <br data-id="element-169" />
              <span className="text-gold drop-shadow-sm" data-id="element-170">
                {t('hero.title2')}
              </span>
            </h1>

            <div className="border-l-4 border-gold pl-6 mb-8 md:mb-10" data-id="element-171">
              <p className="font-serif text-xl md:text-2xl lg:text-3xl text-charcoal-700 leading-relaxed" data-id="element-172">
                {t('hero.description')}
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-4" data-id="element-173">
              <a href="https://podcasts.apple.com/sn/podcast/the-african-founder/id1888801055" target="_blank" rel="noopener noreferrer" className="bg-charcoal-900 text-gold px-8 py-4 font-sans font-bold tracking-wide hover:bg-earth-terracotta hover:text-white transition-colors duration-300 flex items-center justify-center gap-2 group" data-id="element-174">
                {t('hero.startListening')}
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" data-id="element-175" />
              </a>
            </div>
          </motion.div>
        </div>

        {/* Bottom Caption */}
        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 1,
        delay: 0.8
      }} className="mt-12 md:mt-16 text-center" data-id="element-176">
          <p className="font-mono text-sm text-charcoal-500 uppercase tracking-widest" data-id="element-177">
            {t('hero.caption')}
          </p>
        </motion.div>
      </div>

      {/* Scroll Indicator */}
      <motion.div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 text-charcoal-900 flex flex-col items-center gap-2" animate={{
      y: [0, 10, 0]
    }} transition={{
      duration: 2,
      repeat: Infinity,
      ease: 'easeInOut'
    }} data-id="element-178">
        <span className="font-mono text-xs tracking-widest uppercase font-bold text-earth-terracotta" data-id="element-179">
          {t('hero.scroll')}
        </span>
        <ChevronDown className="w-5 h-5 text-gold" data-id="element-180" />
      </motion.div>
    </section>;
}